import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CreditPurchaseModal from './CreditPurchaseModal';
import './LicenseStatusWidget.css';

const LicenseStatusWidget = ({ user }) => {
  const { t } = useTranslation();
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Temporary function to handle upgrade requests
  const handleUpgradeRequest = () => {
    toast.info(t('licenseStatusWidget.upgradeRequest.title') + '\n\n' +
          t('licenseStatusWidget.upgradeRequest.paymentSoon') + '\n' +
          t('licenseStatusWidget.upgradeRequest.contactUs') + '\n' +
          '   • Email: nodemented.contact@gmail.com\n' +
          '   • ' + t('licenseStatusWidget.upgradeRequest.subject') + ' - ' + (user?.email || t('licenseStatusWidget.upgradeRequest.user')) + '\n\n' +
          t('licenseStatusWidget.upgradeRequest.contactSoon'));
  };

  useEffect(() => {
    if (user && !user.isSuperAdmin) {
      loadInitialData();
      // Check every 30 seconds for real-time usage
      const interval = setInterval(loadUsageData, 30 * 1000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadInitialData = async () => {
    await Promise.all([
      checkLicenseStatus(),
      loadUsageData()
    ]);
  };

  const checkLicenseStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/license/my-license`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLicenseStatus(data);
      }
    } catch (error) {
      console.error('❌ Erreur vérification licence widget:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsageData = async () => {
    try {
      setRefreshing(true);
      const response = await fetch(`${API_BASE_URL}/api/usage/current`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsageData(data);
      }
    } catch (error) {
      console.error('❌ Erreur chargement usage:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handlePurchaseSuccess = (quantity) => {
    // Reload data after purchase
    setTimeout(() => {
      loadInitialData();
    }, 1000);
  };

  const getWidgetStatus = () => {
    if (!licenseStatus) return null;

    if (!licenseStatus.hasLicense) {
      return {
        type: 'no-license',
        icon: '🎫',
        title: t('licenseStatusWidget.status.noLicense.title'),
        subtitle: t('licenseStatusWidget.status.noLicense.subtitle'),
        color: 'blue',
        urgent: true,
        action: 'Démarrer trial',
        actionHandler: startFreeTrial
      };
    }

    const license = licenseStatus.license;
    const days = license.daysRemaining;

    if (license.isExpired) {
      return {
        type: 'expired',
        icon: '⚠️',
        title: t('licenseStatusWidget.status.expired.title'),
        subtitle: 'Renouvelez pour continuer',
        color: 'red',
        urgent: true,
        action: 'Renouveler',
        actionHandler: handleUpgradeRequest
      };
    }

    if (days <= 3) {
      return {
        type: 'critical',
        icon: '🚨',
        title: `Expire dans ${days} jour${days > 1 ? 's' : ''}`,
        subtitle: 'Action requise rapidement',
        color: 'red',
        urgent: true,
        action: 'Renouveler',
        actionHandler: handleUpgradeRequest
      };
    }

    if (days <= 7) {
      return {
        type: 'warning',
        icon: '⏰',
        title: t('licenseStatusWidget.status.daysRemaining', { days }),
        subtitle: 'Pensez à renouveler',
        color: 'orange',
        urgent: false,
        action: 'Voir plans',
        actionHandler: () => setExpanded(!expanded)
      };
    }

    if (license.type === 'free_trial') {
      return {
        type: 'trial',
        icon: '💡',
        title: `Trial - ${days} jours`,
        subtitle: `Plan ${license.plan}`,
        color: 'blue',
        urgent: false,
        action: t('licenseStatusWidget.actions.upgrade'),
        actionHandler: () => setExpanded(!expanded)
      };
    }

    // Licence payante active
    return {
      type: 'active',
      icon: '✅',
      title: `Plan ${license.plan}`,
      subtitle: t('licenseStatusWidget.status.daysRemaining', { days }),
      color: 'green',
      urgent: false,
      action: 'Détails',
      actionHandler: () => setExpanded(!expanded)
    };
  };

  const startFreeTrial = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/license/start-trial`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success(t('licenseStatusWidget.actions.trialActivated'));
        checkLicenseStatus();
      } else {
        const error = await response.json();
        toast.error(`❌ ${error.error || 'Impossible d\'activer le trial'}`);
      }
    } catch (error) {
      console.error('Erreur activation trial widget:', error);
      toast.error('❌ Erreur lors de l\'activation du trial');
    }
  };

  const getProgressPercentage = () => {
    if (!licenseStatus?.license) return 0;
    
    const total = 30; // Assumons 30 jours pour les trials/licences
    const remaining = licenseStatus.license.daysRemaining;
    return Math.max(0, Math.min(100, ((total - remaining) / total) * 100));
  };

  // Ne pas afficher pour les super admins
  if (user?.isSuperAdmin || loading) {
    return null;
  }

  const status = getWidgetStatus();
  if (!status) return null;

  return (
    <div className={`license-status-widget license-widget-${status.color} ${status.urgent ? 'license-widget-urgent' : ''}`} id="license-status-widget">
      <div className="license-widget-header" onClick={() => setExpanded(!expanded)}>
        <div className="license-widget-icon">{status.icon}</div>
        <div className="license-widget-info">
          <h4 className="license-widget-title">{status.title}</h4>
          <p className="license-widget-subtitle">{status.subtitle}</p>
        </div>
        <div className="license-widget-actions">
          {status.action && (
            <button 
              className="license-widget-btn"
              onClick={(e) => {
                e.stopPropagation();
                status.actionHandler();
              }}
            >
              {status.action}
            </button>
          )}
          <button className="license-widget-expand" onClick={(e) => e.stopPropagation()}>
            {expanded ? '▼' : '▶'}
          </button>
        </div>
      </div>

      {licenseStatus?.license && !licenseStatus.license.isExpired && (
        <div className="license-widget-progress">
          <div className="license-progress-bar">
            <div 
              className="license-progress-fill"
              style={{ width: `${100 - getProgressPercentage()}%` }}
            ></div>
          </div>
          <span className="license-progress-text">
            {licenseStatus.license.daysRemaining} {t('licenseStatusWidget.time.day', { count: licenseStatus.license.daysRemaining })} {t('licenseStatusWidget.time.remaining', { count: licenseStatus.license.daysRemaining })}
          </span>
        </div>
      )}

      {expanded && (licenseStatus || usageData) && (
        <div className="license-widget-details" id="license-widget-details">
          <div className="license-detail-section">
            <div className="usage-header">
              <h5>📊 {t('licenseStatusWidget.usage.realTimeUsage')}</h5>
              <button 
                className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
                onClick={loadUsageData}
                disabled={refreshing}
                title={t('licenseStatusWidget.usage.refreshUsage')}
              >
                {refreshing ? '⏳' : '🔄'}
              </button>
            </div>
            
            <div className="license-usage-bars">
              {/* Utiliser usageData en priorité, puis licenseStatus en fallback */}
              {(usageData?.usage || (licenseStatus?.usage && licenseStatus?.limits)) && (
                <>
                  {/* API Calls avec possibilité d'achat */}
                  <div className="license-usage-item enhanced">
                    <div className="usage-item-header">
                      <span className="usage-icon">🔌</span>
                      <span className="usage-label">API Calls</span>
                      <span className="usage-percentage">
                        {usageData?.usage?.apiCalls?.percentage || Math.round((licenseStatus.usage.apiCalls / licenseStatus.limits.maxApiCalls) * 100)}%
                      </span>
                    </div>
                    <div className="license-usage-bar">
                      <div 
                        className={`license-usage-fill ${
                          (usageData?.usage?.apiCalls?.percentage || 0) >= 90 ? 'critical' : 
                          (usageData?.usage?.apiCalls?.percentage || 0) >= 80 ? 'warning' : 'normal'
                        }`}
                        style={{ 
                          width: `${Math.min(100, usageData?.usage?.apiCalls?.percentage || 
                            (licenseStatus.usage.apiCalls / licenseStatus.limits.maxApiCalls) * 100)}%` 
                        }}
                      ></div>
                    </div>
                    <div className="usage-item-footer">
                      <span className="usage-numbers">
                        {(usageData?.usage?.apiCalls?.used || licenseStatus.usage.apiCalls || 0).toLocaleString()} / 
                        {(usageData?.usage?.apiCalls?.limit || licenseStatus.limits.maxApiCalls || 0).toLocaleString()}
                      </span>
                      {(usageData?.usage?.apiCalls?.remaining !== undefined) && (
                        <span className="usage-remaining">
                          {usageData.usage.apiCalls.remaining.toLocaleString()} restants
                        </span>
                      )}
                    </div>
                    
                    {/* Bouton d'achat si proche de la limite */}
                    {(usageData?.usage?.apiCalls?.percentage >= 80 || 
                      (licenseStatus?.usage?.apiCalls / licenseStatus?.limits?.maxApiCalls) * 100 >= 80) && (
                      <div className="usage-actions">
                        <button 
                          className="buy-credits-btn"
                          onClick={() => setShowCreditModal(true)}
                        >
                          💳 Acheter des crédits
                        </button>
                      </div>
                    )}
                    
                    {/* Alertes critiques */}
                    {usageData?.alerts?.filter(alert => alert.resource === 'apiCalls').map((alert, index) => (
                      <div key={index} className={`usage-alert ${alert.level}`}>
                        <span className="alert-icon">
                          {alert.level === 'critical' ? '🚨' : '⚠️'}
                        </span>
                        <span className="alert-message">{alert.message}</span>
                      </div>
                    ))}
                  </div>

                  {/* Nœuds */}
                  <div className="license-usage-item enhanced">
                    <div className="usage-item-header">
                      <span className="usage-icon">🔗</span>
                      <span className="usage-label">Nœuds</span>
                      <span className="usage-percentage">
                        {usageData?.usage?.nodes?.percentage || Math.round((licenseStatus.usage.nodes / licenseStatus.limits.maxNodes) * 100)}%
                      </span>
                    </div>
                    <div className="license-usage-bar">
                      <div 
                        className={`license-usage-fill ${
                          (usageData?.usage?.nodes?.percentage || 0) >= 90 ? 'critical' : 
                          (usageData?.usage?.nodes?.percentage || 0) >= 80 ? 'warning' : 'normal'
                        }`}
                        style={{ 
                          width: `${Math.min(100, usageData?.usage?.nodes?.percentage || 
                            (licenseStatus.usage.nodes / licenseStatus.limits.maxNodes) * 100)}%` 
                        }}
                      ></div>
                    </div>
                    <div className="usage-item-footer">
                      <span className="usage-numbers">
                        {(usageData?.usage?.nodes?.used || licenseStatus.usage.nodes || 0).toLocaleString()} / 
                        {(usageData?.usage?.nodes?.limit || licenseStatus.limits.maxNodes || 0).toLocaleString()}
                      </span>
                      {(usageData?.usage?.nodes?.remaining !== undefined) && (
                        <span className="usage-remaining">
                          {usageData.usage.nodes.remaining.toLocaleString()} restants
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Stockage */}
                  <div className="license-usage-item enhanced">
                    <div className="usage-item-header">
                      <span className="usage-icon">💾</span>
                      <span className="usage-label">Stockage</span>
                      <span className="usage-percentage">
                        {usageData?.usage?.storage?.percentage || Math.round((licenseStatus.usage.storage / licenseStatus.limits.maxStorage) * 100)}%
                      </span>
                    </div>
                    <div className="license-usage-bar">
                      <div 
                        className={`license-usage-fill ${
                          (usageData?.usage?.storage?.percentage || 0) >= 90 ? 'critical' : 
                          (usageData?.usage?.storage?.percentage || 0) >= 80 ? 'warning' : 'normal'
                        }`}
                        style={{ 
                          width: `${Math.min(100, usageData?.usage?.storage?.percentage || 
                            (licenseStatus.usage.storage / licenseStatus.limits.maxStorage) * 100)}%` 
                        }}
                      ></div>
                    </div>
                    <div className="usage-item-footer">
                      <span className="usage-numbers">
                        {((usageData?.usage?.storage?.used || licenseStatus.usage.storage || 0)).toFixed(2)} GB / 
                        {(usageData?.usage?.storage?.limit || licenseStatus.limits.maxStorage || 0)} GB
                      </span>
                      {(usageData?.usage?.storage?.remaining !== undefined) && (
                        <span className="usage-remaining">
                          {usageData.usage.storage.remaining.toFixed(2)} GB restants
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="license-detail-section">
            <h5>💎 Plans disponibles</h5>
            <div className="license-plans-mini">
              <div className="license-plan-mini">
                <strong>Starter</strong> - 29€/mois
                <br /><small>100 nœuds, 1K API calls</small>
              </div>
              <div className="license-plan-mini">
                <strong>Pro</strong> - 99€/mois
                <br /><small>500 nœuds, 5K API calls</small>
              </div>
              <div className="license-plan-mini">
                <strong>Enterprise</strong> - 299€/mois
                <br /><small>1K nœuds, 10K API calls</small>
              </div>
            </div>
          </div>

          <div className="license-detail-section">
            <h5>💬 Besoin d'aide ?</h5>
            <div className="license-contact-options">
              <button 
                className="license-contact-btn"
                onClick={() => window.open('mailto:nodemented.contact@gmail.com?subject=Question sur ma licence Nodemented&body=Bonjour,%0A%0AJ\'ai une question concernant ma licence Nodemented.%0A%0AUtilisateur: ' + encodeURIComponent(user?.email || '') + '%0APlan actuel: ' + encodeURIComponent(licenseStatus?.license?.plan || 'Aucun') + '%0A%0AMerci !', '_blank')}
                title="Contacter le support par email"
              >
                📧 Support Email
              </button>
              <button 
                className="license-contact-btn"
                onClick={() => window.open('https://calendly.com/nodemented-demo', '_blank')}
                title="Prendre rendez-vous pour une démo"
              >
                📅 Démo & Conseil
              </button>
              <button 
                className="license-contact-btn"
                onClick={() => window.open('https://discord.gg/nodemented', '_blank')}
                title="Rejoindre notre communauté Discord"
              >
                💬 Communauté
              </button>
            </div>
          </div>

          <div className="license-widget-footer">
            <button 
              className="license-widget-btn license-widget-btn-primary"
              onClick={handleUpgradeRequest}
            >
              Voir tous les plans
            </button>
            <button 
              className="license-widget-btn license-widget-btn-secondary"
              onClick={() => setExpanded(false)}
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Modal d'achat de crédits */}
      <CreditPurchaseModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        currentUsage={usageData?.usage?.apiCalls}
        onPurchaseSuccess={handlePurchaseSuccess}
      />
    </div>
  );
};

export default LicenseStatusWidget;
